import React, { useEffect, useCallback, useMemo } from 'react';
import type { SOFTWARE_PROJECT } from '@atlassian/jira-common-constants/src/project-types';
import { editionAwarenessComparisonModalEntryPoint } from '@atlassian/jira-edition-awareness-comparison-modal/entrypoint';
import { useEntryPoint } from '@atlassian/jira-entry-point';
import { useEntryPointButtonTrigger } from '@atlassian/jira-entry-point-button-trigger';
import { ModalEntryPointContainer } from '@atlassian/jira-entry-point-modal-container';
import { UNSAFE_noExposureExp } from '@atlassian/jira-feature-experiments';
import { type UserCountData, useGetJswUserCount } from '@atlassian/jira-growth-utils';
import { jswUpsellModalEntryPoint } from '@atlassian/jira-jsw-upsell-modal/entrypoint';
import { useIsProductUsingCcp } from '@atlassian/jira-navigation-apps-sidebar-edition-awareness-utils';
import { fireUIAnalytics, useAnalyticsEvents } from '@atlassian/jira-product-analytics-bridge';
import { useCloudId, useIsSiteAdmin } from '@atlassian/jira-tenant-context-controller';
import { LIMITED_USERS_LEFT_COUNT, NO_USERS_LEFT_COUNT } from '../../../common/constants';
import { TOP_NAV, type EALocation } from '../../../common/ui/types';
import { LimitedUsersLeftButton, NoUsersLeftButton, UpgradeButton } from './buttons';

export type UsersRemainingCtaButtonProps = {
	projectType: typeof SOFTWARE_PROJECT;
	location: EALocation;
	siteHasJswOnly: boolean;
};

export function UsersRemainingCtaButton(props: UsersRemainingCtaButtonProps) {
	const { projectType, location, siteHasJswOnly } = props;
	const isUsingCcp = useIsProductUsingCcp(projectType);
	const { createAnalyticsEvent } = useAnalyticsEvents();
	const cloudId = useCloudId();
	const isSiteAdmin = useIsSiteAdmin();

	// eslint-disable-next-line jira/ff/unsafe-no-exposure
	const [config] = UNSAFE_noExposureExp('jsw_jsm_edition_awareness_top_nav_consolidation');
	const eaLocation = useMemo(() => config.get<string>('location', 'not-enrolled'), [config]);

	const shouldUseCache = useCallback(
		(userCountData: UserCountData) => userCountData.unitCount < NO_USERS_LEFT_COUNT,
		[],
	);
	const { data } = useGetJswUserCount({
		cloudId,
		shouldUseCache,
		skip: !isSiteAdmin,
	});

	const userCount = data?.unitCount;

	// Fire viewed analytics once on initial render
	useEffect(() => {
		if (userCount !== undefined) {
			fireUIAnalytics(
				createAnalyticsEvent({
					action: 'viewed',
					actionSubject: 'button',
				}),
				'usersRemainingFreePill',
				{
					userCount,
					location,
					experimentLocation: eaLocation,
				},
			);
		}
	}, [createAnalyticsEvent, userCount, location, eaLocation]);

	const fireButtonAnalytics = useCallback(
		(action: string) =>
			fireUIAnalytics(
				createAnalyticsEvent({
					action,
					actionSubject: 'button',
				}),
				'editionAwarenessFreePill',
				{
					userCount,
					location,
					experimentLocation: eaLocation,
				},
			),
		[createAnalyticsEvent, userCount, location, eaLocation],
	);

	const onButtonClick = () => fireButtonAnalytics('clicked');
	const onHoverButton = () => fireButtonAnalytics('hovered');

	// Show jsw upsell modal if the site only has jsw OR variation cohort in EA top nav experiment
	const showUpsellModal = eaLocation === TOP_NAV || siteHasJswOnly;
	const entryPoint = showUpsellModal
		? jswUpsellModalEntryPoint
		: editionAwarenessComparisonModalEntryPoint;
	const isCcpJswOnlySite = isUsingCcp && siteHasJswOnly;

	const entryPointParams = showUpsellModal ? { cloudIds: [cloudId], isCcpJswOnlySite } : {};

	// The linter doesn't recognise that the rule is being followed due to the conditional entry point
	// eslint-disable-next-line @atlassian/react-entrypoint/prefer-entrypoint-file-import
	const { entryPointActions, entryPointReferenceSubject } = useEntryPoint(
		entryPoint,
		entryPointParams,
	);

	const triggerRef = useEntryPointButtonTrigger(entryPointActions);
	const Button = getUsersRemainingButton(userCount);

	let entryPointSubject;
	if (showUpsellModal) {
		entryPointSubject = (
			<ModalEntryPointContainer
				entryPointReferenceSubject={entryPointReferenceSubject}
				id="jsw-upsell-modal"
				packageName="edition-awareness"
				teamName="Flywheel-Tako"
				runtimeProps={{
					// The TS error is due to the conditional entry point
					// eslint-disable-next-line @typescript-eslint/ban-ts-comment
					// @ts-ignore-error
					isCcpJswOnlySite,
				}}
				entryPointActions={entryPointActions}
			/>
		);
	} else {
		entryPointSubject = (
			<ModalEntryPointContainer
				entryPointReferenceSubject={entryPointReferenceSubject}
				id="edition-awareness-modal"
				packageName="edition-awareness"
				teamName="Flywheel-Tako"
				runtimeProps={{
					// The TS error is due to the conditional entry point
					// eslint-disable-next-line @typescript-eslint/ban-ts-comment
					// @ts-ignore-error
					isUsingCcp,
					projectType,
				}}
				entryPointActions={entryPointActions}
			/>
		);
	}
	return (
		<>
			<Button
				triggerRef={triggerRef}
				onButtonClick={onButtonClick}
				onHoverButton={onHoverButton}
				location={location}
			/>
			{entryPointSubject}
		</>
	);
}

const getUsersRemainingButton = (userCount?: number) => {
	if (userCount === undefined) {
		return UpgradeButton;
	}

	// 10 user limit, show "No Users Left" button
	if (userCount >= NO_USERS_LEFT_COUNT) {
		return NoUsersLeftButton;
	}

	// 7-9 users show "Limited Users Left" button
	if (userCount >= LIMITED_USERS_LEFT_COUNT && userCount < NO_USERS_LEFT_COUNT) {
		return LimitedUsersLeftButton;
	}

	return UpgradeButton;
};
